import { ArrowRight, Search } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import SeoHead from "@shared/components/Seo/SeoHead";
import { BRAND } from "@shared/brand";
import MarketingShell, { FinalCta, ProductFrame } from "./MarketingShell";
import { useCasePages } from "./content";

export default function UseCaseLanding({ slug: slugOverride }) {
  const { slug } = useParams();
  const activeSlug = slugOverride || slug;
  const page = useCasePages[activeSlug];

  if (BRAND.id !== "videobrief" || !page) {
    return <Navigate to="/" replace />;
  }

  const related = Object.entries(useCasePages)
    .filter(([key]) => key !== activeSlug)
    .map(([key, value]) => ({ slug: key, ...value }));

  return (
    <MarketingShell>
      <SeoHead
        title={page.eyebrow.replace("VIDEOBRIEF FOR ", "").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase())}
        description={page.intro}
        path={`/${activeSlug}`}
      />

      <section className="vb-use-case-hero">
        <div className="vb-container vb-use-case-hero__grid">
          <div>
            <span className="vb-eyebrow">{page.eyebrow}</span>
            <h1>
              {page.title}
              <span>{page.accent}</span>
            </h1>
            <p className="vb-lead">{page.intro}</p>
            <div className="vb-actions">
              <Link to="/register" className="vb-button vb-button--primary">
                Try VideoBrief Free <ArrowRight size={17} />
              </Link>
              <a href="#workflow" className="vb-button vb-button--secondary">See the workflow</a>
            </div>
          </div>
          <div className="vb-use-case-hero__visual">
            <ProductFrame
              src={page.image}
              alt={page.imageAlt}
              label={`${page.eyebrow.replace("VIDEOBRIEF FOR ", "")} workflow`}
              eager
            />
          </div>
        </div>
      </section>

      <section className="vb-scenario" id="workflow">
        <div className="vb-container">
          <div className="vb-section-heading vb-section-heading--center">
            <span className="vb-eyebrow">A QUESTION BECOMES AN ASSET</span>
            <h2>Go straight from “do we have this?” to “here it is.”</h2>
          </div>
          <div className="vb-scenario__flow">
            <div className="vb-scenario__card">
              <span>The question</span>
              <strong>“{page.question}”</strong>
            </div>
            <div className="vb-scenario__arrow">→</div>
            <div className="vb-scenario__card">
              <span><Search size={14} /> Search VideoBrief</span>
              <strong>Across every chapter, quote, summary, and source video</strong>
            </div>
            <div className="vb-scenario__arrow">→</div>
            <div className="vb-scenario__card vb-scenario__card--result">
              <span>{page.result}</span>
              <strong>{page.resultDetail}</strong>
              <small>Source and context included</small>
            </div>
          </div>
        </div>
      </section>

      <section className="vb-section vb-section--soft">
        <div className="vb-container">
          <div className="vb-pillar-strip">
            {page.pillars.map((pillar) => <span key={pillar}>{pillar}</span>)}
          </div>
        </div>
      </section>

      <section className="vb-section">
        <div className="vb-container">
          <div className="vb-section-heading">
            <span className="vb-eyebrow">THE VALUE COMPOUNDS</span>
            <h2>Make every recording more useful than the last.</h2>
          </div>
          <div className="vb-outcomes">
            {page.outcomes.map(([title, description], index) => (
              <article className="vb-outcome" key={title}>
                <span className="vb-outcome__number">0{index + 1}</span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="vb-section vb-section--soft">
        <div className="vb-container">
          <div className="vb-section-heading">
            <span className="vb-eyebrow">EXPLORE MORE GTM WORKFLOWS</span>
            <h2>One platform. More ways to use the signal.</h2>
          </div>
          <div className="vb-related-grid">
            {related.map((item) => (
              <Link className="vb-related-card" to={`/${item.slug}`} key={item.slug}>
                <span>{item.eyebrow.replace("VIDEOBRIEF FOR ", "")}</span>
                <h3>{item.title} {item.accent}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCta title="Stop rewatching. Start using what you already have." />
    </MarketingShell>
  );
}
